import type { ObservationRecord } from '../types/observations';
import {
  ALL_WEARABLE_METRIC_IDS,
  DEFAULT_DASHBOARD_METRIC_IDS,
  WEARABLE_METRIC_DEFINITIONS,
  type WearableMetricDefinition,
  type WearableMetricId,
} from '../data/wearableMetrics';

export interface WearableMetricPoint {
  key: string;
  label: string;
  observedAt: string;
  value: number;
}

export interface SleepStagesPoint {
  key: string;
  label: string;
  observedAt: string;
  light: number;
  deep: number;
  rem: number;
  awake: number;
}

interface WearableMetricDatasetBase {
  id: WearableMetricId;
  definition: WearableMetricDefinition;
  latestObservedAt: string | null;
  latestValueDisplay: string;
  averageValueDisplay: string | null;
  deltaLabel: string;
  hasData: boolean;
}

export interface NumericWearableMetricDataset extends WearableMetricDatasetBase {
  chartType: 'line' | 'bar';
  points: WearableMetricPoint[];
}

export interface SleepStagesWearableMetricDataset extends WearableMetricDatasetBase {
  chartType: 'sleep_stages';
  points: SleepStagesPoint[];
}

export type WearableMetricDataset = NumericWearableMetricDataset | SleepStagesWearableMetricDataset;

const DAY_MS = 24 * 60 * 60 * 1000;

function toDateKey(value?: string | null) {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function toLabel(dateKey: string) {
  const date = new Date(`${dateKey}T00:00:00Z`);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatHours(value: number) {
  return `${value.toFixed(1)}h`;
}

function formatMinutes(value: number) {
  return `${Math.round(value)} min`;
}

function formatPercent(value: number) {
  return `${Math.round(value)}%`;
}

function formatNumber(value: number, digits = 0) {
  return value.toFixed(digits);
}

export function formatWearableMetricValue(metricId: WearableMetricId, value: number) {
  switch (metricId) {
    case 'resting_heart_rate':
      return `${Math.round(value)} bpm`;
    case 'recovery_score':
    case 'sleep_efficiency':
    case 'sleep_consistency':
      return formatPercent(value);
    case 'hrv':
      return `${Math.round(value)} ms`;
    case 'skin_temperature':
      return `${value.toFixed(2)} C`;
    case 'sleep_duration':
      return formatHours(value);
    case 'respiratory_rate':
      return `${value.toFixed(1)} rpm`;
    case 'day_strain':
    case 'workout_strain':
      return formatNumber(value, 1);
    case 'energy_burned':
      return `${Math.round(value).toLocaleString()} kcal`;
    case 'workout_duration':
      return formatMinutes(value);
    case 'sleep_stages':
      return formatMinutes(value);
    default:
      return String(value);
  }
}

function formatAverageValue(metricId: WearableMetricId, values: number[]) {
  if (!values.length) return null;
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  return formatWearableMetricValue(metricId, average);
}

/**
 * Whether an observation belongs to a given wearable metric, and its value in
 * that metric's canonical unit. Exported so the dashboard's period-over-period
 * delta util reads observations exactly the way the charts do — matching rules
 * here are per-metric and easy to get subtly wrong twice.
 */
export function matchMetricObservation(metricId: WearableMetricId, observation: ObservationRecord) {
  const display = String(observation.display || '').toLowerCase();
  const code = String(observation.code || '').toLowerCase();
  const sourceDocumentId = String(observation.sourceDocumentId || '').toLowerCase();

  switch (metricId) {
    case 'resting_heart_rate':
      return display.includes('resting heart rate') || display === 'heart rate' || display === 'resting hr' || code.includes('resting_heart_rate') || code.includes('heart_rate');
    case 'recovery_score':
      return display.includes('recovery score') || code.includes('recovery_score');
    case 'hrv':
      return display.includes('heart rate variability') || display === 'hrv' || code.includes('heart_rate_variability') || code === 'hrv';
    case 'skin_temperature':
      return display.includes('skin temp') || display.includes('skin temperature') || code.includes('skin_temperature') || code.includes('skin_temp');
    case 'sleep_duration':
      return display.includes('asleep duration') || display.includes('sleep duration') || code.includes('sleep_duration');
    case 'sleep_efficiency':
      return display.includes('sleep efficiency') || code.includes('sleep_efficiency');
    case 'sleep_consistency':
      return display.includes('sleep consistency') || code.includes('sleep_consistency');
    case 'respiratory_rate':
      return display.includes('respiratory rate') || code.includes('respiratory_rate');
    case 'day_strain':
      return display.includes('day strain') || code.includes('day_strain');
    case 'energy_burned':
      return display.includes('energy burned') || code.includes('energy_burned');
    case 'workout_strain':
      return display.includes('activity strain') || display.includes('workout strain') || code.includes('workout_strain') || code.includes('activity_strain');
    case 'workout_duration':
      return display.includes('workout duration')
        || display.includes('activity duration')
        || code.includes('workout_duration')
        || code.includes('activity_duration')
        || ((display === 'duration' || display.includes('duration (')) && sourceDocumentId.includes('workout'));
    default:
      return false;
  }
}

export function normalizeMetricValue(metricId: WearableMetricId, observation: ObservationRecord) {
  const value = observation.valueNumber;
  if (value == null || Number.isNaN(value)) return null;

  switch (metricId) {
    case 'sleep_duration':
      return observation.unit === 'min' ? value / 60 : value;
    case 'workout_duration':
      return value;
    default:
      return value;
  }
}

function buildNumericDataset(metricId: Exclude<WearableMetricId, 'sleep_stages'>, observations: ObservationRecord[]): NumericWearableMetricDataset {
  const definition = WEARABLE_METRIC_DEFINITIONS[metricId];
  const grouped = new Map<string, { observedAt: string; values: number[] }>();

  observations
    .filter((observation) => matchMetricObservation(metricId, observation))
    .forEach((observation) => {
      const value = normalizeMetricValue(metricId, observation);
      const observedAt = observation.observedAt || null;
      const dateKey = toDateKey(observedAt);
      if (value == null || !dateKey) return;
      const entry = grouped.get(dateKey) || { observedAt: observedAt || `${dateKey}T00:00:00Z`, values: [] };
      entry.values.push(value);
      if ((observedAt || '') > entry.observedAt) {
        entry.observedAt = observedAt || entry.observedAt;
      }
      grouped.set(dateKey, entry);
    });

  const points = Array.from(grouped.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .slice(-30)
    .map(([dateKey, entry]) => {
      const value = metricId === 'day_strain' || metricId === 'energy_burned' || metricId === 'workout_duration' || metricId === 'workout_strain'
        ? entry.values.reduce((sum, current) => sum + current, 0)
        : entry.values.reduce((sum, current) => sum + current, 0) / entry.values.length;
      return {
        key: dateKey,
        label: toLabel(dateKey),
        observedAt: entry.observedAt,
        value,
      };
    });

  const latestPoint = points[points.length - 1];
  const latestObservedAt = latestPoint?.observedAt || null;

  return {
    id: metricId,
    definition,
    chartType: definition.chartType as 'line' | 'bar',
    points,
    latestObservedAt,
    latestValueDisplay: latestPoint ? formatWearableMetricValue(metricId, latestPoint.value) : 'No data yet',
    averageValueDisplay: points.length ? formatAverageValue(metricId, points.map((point) => point.value)) : null,
    deltaLabel: points.length > 0 ? `${Math.min(points.length, 30)} data points` : 'No data',
    hasData: points.length > 0,
  };
}

function buildSleepStagesDataset(observations: ObservationRecord[]): SleepStagesWearableMetricDataset {
  const definition = WEARABLE_METRIC_DEFINITIONS.sleep_stages;
  const grouped = new Map<string, SleepStagesPoint>();

  observations.forEach((observation) => {
    const display = String(observation.display || '').toLowerCase();
    const value = observation.valueNumber;
    const observedAt = observation.observedAt || null;
    const dateKey = toDateKey(observedAt);
    if (value == null || Number.isNaN(value) || !dateKey) return;

    let stageKey: 'light' | 'deep' | 'rem' | 'awake' | null = null;
    if (display.includes('light sleep duration')) stageKey = 'light';
    if (display.includes('deep')) stageKey = 'deep';
    if (display.includes('rem duration')) stageKey = 'rem';
    if (display.includes('awake duration')) stageKey = 'awake';
    if (!stageKey) return;

    const current = grouped.get(dateKey) || {
      key: dateKey,
      label: toLabel(dateKey),
      observedAt: observedAt || `${dateKey}T00:00:00Z`,
      light: 0,
      deep: 0,
      rem: 0,
      awake: 0,
    };
    current[stageKey] = value;
    if ((observedAt || '') > current.observedAt) {
      current.observedAt = observedAt || current.observedAt;
    }
    grouped.set(dateKey, current);
  });

  const points = Array.from(grouped.values())
    .sort((left, right) => left.key.localeCompare(right.key))
    .slice(-14);
  const latestPoint = points[points.length - 1];
  const latestObservedAt = latestPoint?.observedAt || null;
  const latestTotal = latestPoint ? latestPoint.light + latestPoint.deep + latestPoint.rem : 0;

  return {
    id: 'sleep_stages',
    definition,
    chartType: 'sleep_stages',
    points,
    latestObservedAt,
    latestValueDisplay: latestPoint ? formatMinutes(latestTotal) : 'No data yet',
    averageValueDisplay: points.length
      ? formatMinutes(points.reduce((sum, point) => sum + point.light + point.deep + point.rem, 0) / points.length)
      : null,
    deltaLabel: points.length > 0 ? `${Math.min(points.length, 14)} nights` : 'No data',
    hasData: points.length > 0,
  };
}

export function buildWearableMetricDatasets(observations: ObservationRecord[]) {
  return ALL_WEARABLE_METRIC_IDS.reduce<Record<WearableMetricId, WearableMetricDataset>>((accumulator, metricId) => {
    accumulator[metricId] = metricId === 'sleep_stages'
      ? buildSleepStagesDataset(observations)
      : buildNumericDataset(metricId, observations);
    return accumulator;
  }, {} as Record<WearableMetricId, WearableMetricDataset>);
}

export function sanitizeDashboardMetricIds(value: unknown): WearableMetricId[] {
  if (!Array.isArray(value)) return DEFAULT_DASHBOARD_METRIC_IDS;
  const unique = value.filter((metricId): metricId is WearableMetricId => ALL_WEARABLE_METRIC_IDS.includes(metricId as WearableMetricId));
  const deduped = Array.from(new Set(unique));
  if (deduped.length === 4) return deduped;
  return DEFAULT_DASHBOARD_METRIC_IDS;
}

export function getWearableMetricDatasetSummary(dataset: WearableMetricDataset) {
  if (!dataset.hasData) {
    return 'No parsed wearable data is available for this metric yet.';
  }

  if (dataset.chartType === 'sleep_stages') {
    const latestPoint = dataset.points[dataset.points.length - 1];
    if (!latestPoint) return 'No parsed wearable data is available for this metric yet.';
    return `Latest night captured ${formatMinutes(latestPoint.light)} light sleep, ${formatMinutes(latestPoint.deep)} deep sleep, ${formatMinutes(latestPoint.rem)} REM, and ${formatMinutes(latestPoint.awake)} awake time.`;
  }

  const latestPoint = dataset.points[dataset.points.length - 1];
  const firstPoint = dataset.points[0];
  if (!latestPoint || !firstPoint) {
    return 'No parsed wearable data is available for this metric yet.';
  }

  const difference = latestPoint.value - firstPoint.value;
  const trendWord = Math.abs(difference) < 0.01 ? 'held broadly steady' : difference > 0 ? 'moved upward' : 'moved downward';
  return `${dataset.definition.label} has ${trendWord} across the current wearable window, with a latest value of ${formatWearableMetricValue(dataset.id, latestPoint.value)}${dataset.averageValueDisplay ? ` and an average around ${dataset.averageValueDisplay}` : ''}.`;
}

export function getWearableMetricDateLabel(value?: string | null) {
  if (!value) return 'Date not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const daysAgo = Math.max(0, Math.round((Date.now() - date.getTime()) / DAY_MS));
  const absolute = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  if (daysAgo === 0) return `Today (${absolute})`;
  if (daysAgo === 1) return `Yesterday (${absolute})`;
  return `${absolute}`;
}
